import { Component, ViewChild, inject, signal } from '@angular/core';
import { AsyncPipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { combineLatest, map, switchMap, tap } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { SocialUser } from '@abacritt/angularx-social-login';
import { cpf } from 'cpf-cnpj-validator';
import { ButtonComponent } from '@aprova-digital/design-system';
import { queryParam } from '../../signals';

import { Types } from '../../../domain/types.domain';

import { AppHandler } from '../../app.handler';
import {
  InputComponent,
  RecaptchaComponent,
  IconComponent,
  GoogleButtonModule,
} from '../../../@ads/components';
import { RedirectService } from '../../redirect.service';

import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { UserRepositoryInstruction } from '../../../builder/instructions/repositories/user.repository.instructions';
import { LoginByIntegrationServiceInstruction } from '../../../builder/instructions/services/login-by-integration.service.instruction';
import { ResendAccountConfirmationServiceInstruction } from '../../../builder/instructions/services/resend-account-service.instruction';
import { GetConfigByCityIdOutput } from '../../../domain/auth/dtos/get-config-by-city-id.output';
import { PageTitleComponent } from '../../components/abstracts/page-title.component';
import { accountConflictError } from '../../utils/account-conflict';
import { buildEncodedState } from '../../utils/state-handler';
import {
  OidcContinuation,
  govBrAccountCreationUrl,
  govBrAuthorizeBaseUrl,
  govBrLoginUrl,
} from '../../utils/govbr-authorize';
import { IaModeMobileLayout } from '../../utils/ia-mode-mobile';
import { isValidCnpjAlfa } from '../../utils/cnpj.validator';

const REMEMBER_ME_COOKIE_NAME = 'username';
const REMEMBER_ME_COOKIE_EXPIRES = 7; // number of days

@Component({
  selector: 'app-login-by-form',
  standalone: true,
  imports: [
    AsyncPipe,
    NgOptimizedImage,
    NgTemplateOutlet,
    ReactiveFormsModule,
    InputComponent,
    ButtonComponent,
    RecaptchaComponent,
    IconComponent,
    GoogleButtonModule,
  ],
  providers: [
    CookieService,
    new AuthRepositoryInstruction(),
    new UserRepositoryInstruction(),
    new LoginByIntegrationServiceInstruction(),
    new ResendAccountConfirmationServiceInstruction(),
  ],
  templateUrl: './login-by-form.component.html',
  styleUrl: './login-by-form.component.scss',
})
export class LoginByFormComponent extends PageTitleComponent('Login') {
  @ViewChild('inputEmail', { static: false, read: InputComponent })
  public inputEmail: InputComponent | undefined;

  @ViewChild('inputPassword', { static: false })
  public inputPassword: InputComponent | undefined;

  private readonly _router = inject(Router);
  protected readonly iaModeMobile = inject(IaModeMobileLayout);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appHandler = inject(AppHandler);
  private readonly _redirectService = inject(RedirectService);
  private readonly _cookieService = inject(CookieService);
  private readonly _sanitizer = inject(DomSanitizer);

  private readonly _loginByIntegrationService = inject(
    Types.LoginByIntegrationService
  );
  private readonly _authRepository = inject(Types.AuthRepository);
  private readonly _resendAccountConfirmationService = inject(
    Types.ResendAccountConfirmationService
  );

  protected readonly data$ = combineLatest({
    loading: this._appHandler
      .loadingChange()
      .pipe(map((change) => change.loading)),
    city: this._route.data.pipe(
      map(({ city }) => ({
        cityIndex: city.index,
        cityIdentity: city.identidade,
        cityConnection: city.connection,
        cityActive: city.active,
      })),
      tap((data) => {
        if (!data.cityActive) {
          this._appHandler.setLoading(false);
          this._appHandler.hideBackButton(true);
          this._appHandler.setError(true, {
            iconName: 'exclamation-outline',
            iconSize: '48px',
            message: 'Indisponível',
            description: 'Consulte a organização para maiores esclarecimentos',
            options: {
              iconBackgroundColor: '#fff',
              messageClasses: 'font-bold',
              messageHorizontalAlign: 'center',
            },
          });
          return;
        }

        this.cityIndex = data.cityIndex;
        this.cityIdentity = data.cityIdentity;
        this.cityConnection = data.cityConnection;

        this._appHandler.setLoading(false);
        this.isRememberMeActive();
      })
    ),
    config: this._route.data.pipe(
      switchMap(({ city }) =>
        this._authRepository.getConfigByCityId(city.index)
      ),
      tap((config) => {
        if (config.background?.length) {
          this.backgroudImageUrl.set(config.background);
        }

        if (config.hideSignUp) {
          this.hideSignUp.set(true);
        }

        if (config.allowUseCnpj) {
          this.allowUseCnpj.set(true);
        }

        if (config.hideRecoverPassword) {
          this.hideRecoverPassword.set(true);
        }

        const credentialsMethod = config.methods.find((method) => {
          return method.strategy === 'CREDENTIALS';
        });

        if (credentialsMethod?.config) {
          if (credentialsMethod.config['identifierType']) {
            this.identifierType = credentialsMethod.config['identifierType'];
          }

          if (credentialsMethod.config['subtitle']) {
            this.subtitle.set(credentialsMethod.config['subtitle']);
          }
        }

        if (this.identifierType === 'email') {
          this.form.controls.email.setValidators([
            Validators.required,
            this.validateEmail,
          ]);
        } else if (this.identifierType === 'document') {
          this.form.controls.email.setValidators([
            Validators.required,
            this.validateDocumentFn,
          ]);
        } else {
          this.form.controls.email.setValidators([Validators.required]);
        }
      })
    ),
  });

  public routeState = this._router.getCurrentNavigation()?.extras?.state;

  public logginIn = signal(false);
  public backgroudImageUrl = signal<string | null>(null);
  public subtitle = signal('');
  public hideSignUp = signal(false);
  public hideRecoverPassword = signal(false);
  public allowUseCnpj = signal(false);
  public identifierType: 'email' | 'document' | 'registration' = 'email';
  public identifierConfig = {
    email: {
      label: 'E-mail',
      placeholder: 'Digite seu e-mail',
      divider: 'e-mail',
      mask: '',
      errorMessages: {
        required: 'Ops! E-mail precisa ser preenchido.',
        email: 'Ops! Email inválido.',
        incorrect: 'E-mail ou senha incorretos.',
      },
    },
    document: {
      label: 'CPF/CNPJ',
      placeholder: 'CPF/CNPJ',
      divider: 'CPF ou CNPJ',
      mask: '000.000.000-00||AA.AAA.AAA/AAAA-00',
      errorMessages: {
        required: 'Ops! CPF/CNPJ precisa ser preenchido.',
        cpfCnpj: 'Ops! CPF/CNPJ inválido.',
        incorrect: 'CPF/CNPJ ou senha incorretos.',
      },
    },
    registration: {
      label: 'Matrícula',
      placeholder: 'Digite sua matrícula',
      divider: 'matrícula',
      mask: '',
      errorMessages: {
        required: 'Ops! Matrícula precisa ser preenchida.',
        incorrect: 'Matrícula ou senha incorretos.',
      },
    },
  };
  public showPassword = signal(false);
  public rememberMe = signal(false);
  public urlCopied = signal(false);

  public cityLogo: string | undefined = undefined;
  public cityIndex: number = this._route.snapshot.data['city']['index'];
  public cityIdentity: string = this._route.snapshot.data['city']['identidade'];
  public cityConnection: string =
    this._route.snapshot.data['city']['connection'];

  public form = new FormGroup({
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    recaptchaReactive: new FormControl(''),
  });

  public isSocialLoginEnabled = signal(false);
  public isCertificateLoginEnabled = signal(false);
  public sessionTimeout = signal(false);
  public isAccountConfirmed = signal(false);
  public customUrlRedirect: string = '';

  private readonly redirectToParam = queryParam('redirectTo');
  private readonly customUrlRedirectParam = queryParam('customUrlRedirect');

  constructor() {
    super();
    this.setSessionTimeout();
    this.setAccountConfirmed();
  }

  ngOnInit(): void {
    this._appHandler.setBackButtonHome(true);
    this._appHandler.setLoading(true);
    this.storeQueryParams();
  }

  private storeQueryParams(): void {
    try {
      const { customUrlRedirect } = this._route.snapshot.queryParams;
      this.customUrlRedirect = decodeURIComponent(
       customUrlRedirect || ''
      ).replace(/\/+$/, '');
    } catch (err) {
      console.log('Erro ao armazenar queryParams', {
        err,
        queryParams: this._route?.snapshot?.queryParams,
      });
    }
  }

  isRememberMeActive(): void {
    const username = this._cookieService.get(REMEMBER_ME_COOKIE_NAME);
    if (username) {
      this.form.controls.email.setValue(username);
      this.rememberMe.set(true);
      this.inputPassword?.inputRef?.nativeElement.focus();
    }
  }

  redirectToCreateAccount(): void {
    // No celular do IA mode, pessoa física é o padrão; "Sou empresa" fica no próprio cadastro.
    this._router.navigate([this.iaModeMobile.active() ? 'create-account' : 'select-account-type'], {
      relativeTo: this._route,
    });
  }

  redirectToRecoverPassword(): void {
    this._router.navigate(['recover-password'], {
      relativeTo: this._route,
    });
  }

  toggleInputPassword(): void {
    this.showPassword.update((val) => !val);
  }

  handleRemembermeChange(): void {
    this.rememberMe.update((val) => !val);
  }

  onFormInput(): void {
    if (this.form.invalid && this.form.dirty && this.form.touched) {
      this.form.controls.email.setErrors(null);
      this.form.controls.password.setErrors(null);
      this.form.markAsPristine();
      this.form.markAllAsTouched();
    }
  }

  onSubmit(): void {
    if (!this.form.controls.email.value) {
      this.form.controls.email.setErrors({ required: true });
      this.form.controls.email.markAsDirty();
      this.form.controls.email.markAsTouched();
    }

    if (!this.form.controls.password.value) {
      this.form.controls.password.setErrors({ required: true });
      this.form.controls.password.markAsDirty();
      this.form.controls.password.markAsTouched();
    }

    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      return;
    }

    if (this.identifierType === 'email') {
      this.form.controls.email.setValue(
        this.form.controls.email.value.trim().toLowerCase()
      );
    }

    if (this.identifierType === 'document') {
      // CNPJ alfanumérico é sempre em maiúsculas — normaliza antes de autenticar.
      this.form.controls.email.setValue(
        this.form.controls.email.value.toUpperCase()
      );
    }

    this._appHandler.setError(false);
    this.logginIn.set(true);

    this._loginByIntegrationService
      .handle({
        strategy: 'CREDENTIALS',
        connection: this.cityConnection,
        cityId: this.cityIndex,
        email: this.form.controls.email.value,
        password: this.form.controls.password.value,
        recaptcha: this.form.value.recaptchaReactive,
        customUrlRedirect: this.customUrlRedirect,
      })
      .then((response: any) => {
        // No app (IA mode no celular) não há "Lembrar de mim": o identificador fica sempre guardado.
        if (this.rememberMe() || this.iaModeMobile.active()) {
          this._cookieService.set(
            REMEMBER_ME_COOKIE_NAME,
            this.form.controls.email.value,
            REMEMBER_ME_COOKIE_EXPIRES
          );
        } else {
          this._cookieService.delete(REMEMBER_ME_COOKIE_NAME);
        }

        if (response.requireChangePassword) {
          this._router.navigate(['change-password'], {
            relativeTo: this._route,
            queryParams: {
              email: response.email,
              token: response.token,
              type: response.type,
              customUrlRedirect: this.customUrlRedirect,
            },
          });
          return;
        }

        if (response.requireCompleteRegistration) {
          this._router.navigate(
            [`/${this.cityIdentity}/complete-registration`],
            {
              state: {
                userData: response.userData,
                token: response.tempToken,
                redirectUri: `${response.redirectUri}?code=${response.code}`,
              },
            }
          );
          return;
        }

        if (!response.requireTwoFactorAuth) {
          this.redirectTo(response);
          return;
        }

        const queryParams: Record<string, any> = {};

        if (this.customUrlRedirectParam()) {
          queryParams['customUrlRedirect'] = encodeURIComponent(this.customUrlRedirectParam()!);
        }

        if (this.redirectToParam()) {
          queryParams['redirectTo'] = encodeURIComponent(this.redirectToParam()!);
        }

        // SSO (fluxo /sso): `returnTo` é o /oauth/authorize que o /oauth/session
        // precisa receber pra responder 302 em vez de 204. Sem ele o browser fica
        // parado no spinner do 2FA (APR-2841).
        // Lido do snapshot cru de propósito: `returnTo` carrega uma URL com query
        // aninhada (redirect_uri, state) e o signal `queryParam()` aplica um
        // decodeURIComponent extra que corromperia esse encoding. É o mesmo valor
        // que o caminho sem 2FA repassa em `additionalParams` (ver redirectTo()).
        const returnTo = this._route.snapshot.queryParams['returnTo'];
        if (returnTo) {
          queryParams['returnTo'] = encodeURIComponent(returnTo);
        }

        this._router.navigate(['two-factor-authentication'], {
          relativeTo: this._route,
          queryParams,
          state: {
            verificationToken: response.code,
            twoFactorAuthenticationName: response.twoFactorAuthName,
            ...(response.phoneNumber && {
              phoneNumber: response.phoneNumber,
            }),
            customUrlRedirect: this.customUrlRedirectParam(),
            redirectTo: this.redirectToParam(),
          },
        });
      })
      .catch((errorResponse: any) => {
        this.handleLoginError(errorResponse);
      });
  }

  handleLoginError(errorResponse?: any): void {
    this.logginIn.set(false);

    // Nem todo erro que chega aqui é uma HttpErrorResponse: uma exceção lançada
    // no fluxo de sucesso (ex.: redirect bloqueado pela allowlist) cai no mesmo
    // catch e não tem `error`. Sem o acesso opcional, o próprio handler quebra
    // e o usuário fica sem mensagem.
    const errorCode = errorResponse?.error?.code;

    if (errorCode === 'incorretEmailPassword') {
      this.form.controls.email.setErrors({ incorrect: true });
      this.form.controls.password.setErrors({ incorrect: true });
      this.form.controls.email.markAsDirty();
      this.form.controls.password.markAsDirty();
      this.form.controls.recaptchaReactive.reset();
      this.form.controls.recaptchaReactive.markAsDirty();
      this.form.updateValueAndValidity();
      return;
    }

    if (errorCode === 'cityNotActive') {
      this._appHandler.hideBackButton(true);
      this._appHandler.setError(true, {
        iconName: 'exclamation-outline',
        iconSize: '48px',
        message: 'Indisponível',
        description: 'Consulte a organização para maiores esclarecimentos',
        options: {
          iconBackgroundColor: '#fff',
          messageClasses: 'font-bold',
          messageHorizontalAlign: 'center',
        },
      });
      return;
    }

    if (errorCode === 'userBlocked') {
      this._appHandler.setError(true, {
        message: 'Sua conta está bloqueada.',
        description:
          'Contate-nos através do chat, localizado no ícone no canto inferior direito, para prosseguir.',
      });
      this._appHandler.setBackButtonHome(false);
      return;
    }

    const accountConflict = accountConflictError(errorResponse?.error);
    if (accountConflict) {
      this._appHandler.setError(true, accountConflict);
      this._appHandler.setBackButtonHome(false);
      return;
    }

    if (errorCode === 'emailNotVerified') {
      this._appHandler.setError(true, {
        message: 'A verificação da conta está pendente.',
        description:
          'Ação necessária para acessar o sistema. Clique no botão abaixo.',
        actionText: 'Reenviar e-mail de confirmação de conta',
        action: () => {
          this.resendAccountConfirmation(errorResponse?.error?.params?.userId);
        },
      });

      return;
    }

    if (
      errorCode === 'missingUserSectors' ||
      errorCode === 'missingUserAuthorizationGroups'
    ) {
      this._appHandler.setError(true, {
        message: 'Ação necessária para o seu usuário acessar o sistema.',
        description:
          'Seu usuário ainda não possui: <strong>Permissão</strong> <br/><br/> Entre em contato com o Administrador do sistema da organização para solicitar essas liberações.',
        options: {
          useHtmlInMessage: true,
        },
      });
      return;
    }

    if (errorCode === 'inviteNotAccepted') {
      this._appHandler.setError(true, {
        message: 'Seu cadastro não foi completado.',
        description:
          'Clique no botão abaixo para completar seu cadastro e retomar o acesso ao sistema.',
        actionText: 'Completar Cadastro',
        action: () => {
          window.location.href = errorResponse?.error?.params?.signUpUrl;
        },
      });

      return;
    }

    if (errorCode === 'integrationError') {
      this._appHandler.setError(true, {
        message: 'Erro ao autenticar o usuário via integração',
        description: 'Por favor, tente novamente ou entre em contato com o suporte para resolver o problema.',
        actionText: 'Tentar novamente',
        action: () => {
          this._appHandler.clearError();
        },
      });

      return;
    }

    this._appHandler.setError(true, {
      message: 'A operação não pôde ser concluída.',
      description:
        'Por favor, revise seus dados antes de fazer uma nova tentativa. Se o problema persistir, entre em contato com nossa equipe de suporte.',
      actionText: 'Tentar novamente',
      action: () => {
        this._appHandler.clearError();
      },
    });
  }

  loginGovBr(config: GetConfigByCityIdOutput) {
    /*
     * O `redirectTo`/`returnTo` vão dentro do `state` porque a URL de retorno do
     * gov.br é fixa por cidade (`/{cidade}/callback`) e não aceita parâmetro nosso.
     * Sem isso, o login por gov.br a partir de uma aplicação OIDC completava e caía
     * no app padrão da cidade, em vez de voltar para quem pediu o login — o caminho
     * por e-mail e senha sobrevive só porque os parâmetros ficam na URL.
     *
     * Vêm da query da rota, que é a mesma fonte que o `redirectTo(response)` usa no
     * fluxo por senha.
     */
    location.href = govBrLoginUrl(
      govBrAuthorizeBaseUrl(config)!,
      this.customUrlRedirect,
      this.oidcContinuation()
    );
  }

  loginBb(config: GetConfigByCityIdOutput) {
    this.redirectToAuthorizeUrl(config, 'BANCO_DO_BRASIL');
  }

  /*
   * Ida para o /authorize de um provedor externo genérico (hoje, Banco do Brasil).
   * Mesma regra do gov.br: a continuação OIDC viaja no `state`, porque a URL de
   * retorno do provedor é fixa por cidade.
   */
  private redirectToAuthorizeUrl(
    config: GetConfigByCityIdOutput,
    strategy: string
  ) {
    const authMethod = config.methods.find(
      (method) => method.strategy === strategy
    );
    const urlObj = new URL(authMethod?.config['url']);

    const stateValue = buildEncodedState(
      this.customUrlRedirect,
      urlObj.searchParams.get('state') || '',
      { ...this.oidcContinuation() }
    );
    urlObj.searchParams.set('state', stateValue);
    location.href = decodeURIComponent(urlObj.toString());
  }

  createAccountWithGovBr(config: GetConfigByCityIdOutput) {
    this._redirectService.toProvider(
      govBrAccountCreationUrl(
        govBrAuthorizeBaseUrl(config)!,
        this.customUrlRedirect,
        this.oidcContinuation()
      )
    );
  }

  showGovBrAccountCreation(config: GetConfigByCityIdOutput) {
    return (
      config.allowUseCnpj &&
      this.showGovBrMethod(config) &&
      !this.showAuthMethod(config, 'CREDENTIALS')
    );
  }

  private oidcContinuation(): OidcContinuation {
    const query = this._route.snapshot.queryParams;
    return { redirectTo: query['redirectTo'], returnTo: query['returnTo'] };
  }

  private setSessionTimeout(): void {
    const sessionTimeout =
      this._route.snapshot?.queryParams?.['sessionTimeout'];
    this.sessionTimeout.set(parseInt(sessionTimeout) === 1);
  }

  private setAccountConfirmed(): void {
    const queryParams = this._route.snapshot.queryParams;
    if (queryParams && queryParams['accountConfirmed']) {
      this.isAccountConfirmed.set(true);
    }
  }

  async loginICP() {
    this._appHandler.setLoading(true);
    this._router.navigate(['icp'], {
      relativeTo: this._route,
      state: { customUrlRedirect: this.customUrlRedirect },
    });
  }

  private validateEmail = (control: AbstractControl) => {
    if (!control.value) return null;

    const regex = /\S+@\S+\.\S+/;
    return regex.test(control.value) ? null : { email: true };
  };

  private validateDocumentFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    // Até 11 caracteres = CPF (só dígitos); acima = CNPJ, que agora pode ser
    // alfanumérico (IN RFB 2.229/2024) e é validado por isValidCnpjAlfa.
    const isCnpj = this.form.controls.email.value.length > 11;
    const valid = isCnpj
      ? isValidCnpjAlfa(control.value)
      : cpf.isValid(control.value);
    return valid ? null : { cpfCnpj: true };
  };

  private redirectTo(response: any) {
    const { redirectTo, ...additionalParams }: any =
      this._route.snapshot.queryParams;

    if (response.requireNewUser) {
      this._router.navigate([`/${this.cityIdentity}/create-account`], {
        queryParams: {
          skipAccountConfirmation: !response.options?.emailVerification,
        },
        state: {
          initialAccountData: response.userData,
          blockedFields: response.options?.blockFields,
        },
      });

      return;
    }

    this._redirectService.postAuth({
      redirectUri: response.redirectUri,
      code: response.code,
      redirectTo: redirectTo || undefined,
      customUrlRedirect: this.customUrlRedirectParam(),
      additionalParams,
    });
  }

  private resendAccountConfirmation(userId: string): void {
    this._appHandler.setError(true, {
      message: 'Enviando e-mail de confirmação de conta...',
      description:
        'Este processo pode levar alguns segundos. Por favor, aguarde.',
    });

    this._resendAccountConfirmationService
      .handle({
        cityId: this.cityIndex,
        userId,
      })
      .then(() => {
        this._appHandler.setError(true, {
          message: 'E-mail enviado com sucesso!',
          description:
            'Verifique sua caixa de entrada e siga as instruções para confirmar sua conta.',
        });
      })
      .catch((errorResponse: any) => {
        if (errorResponse?.error?.code === 'recentVerificationRequest') {
          this._appHandler.setError(true, {
            message: 'Já enviamos o e-mail de confirmação',
            description:
              'Verifique sua caixa de entrada e a pasta de spam e siga as instruções para confirmar sua conta. Se não encontrar o e-mail, aguarde algumas horas para pedir um novo envio.',
          });
          return;
        }

        this._appHandler.setError(true, {
          message: 'Erro ao enviar o e-mail de confirmação de conta!',
          description:
            'Tente novamente ou entre em contato com nossa equipe de suporte.',
          actionText: 'Tentar novamente',
          action: () => {
            this.resendAccountConfirmation(userId);
          },
        });
      });
  }

  showAuthMethod(config: GetConfigByCityIdOutput, strategy: string) {
    return config.methods.some((method) => method.strategy === strategy);
  }

  showAuthMethodsDivider(config: GetConfigByCityIdOutput) {
    return (
      this.showAuthMethod(config, 'CREDENTIALS') && config.methods.length > 1
    );
  }

  showSingleLoginInstructions(config: GetConfigByCityIdOutput) {
    if (config.methods.length === 0 || config.methods.length > 1) {
      return '';
    }

    const singleLoginInstructions =
      config.methods[0].config?.['singleLoginInstructions'];
    return singleLoginInstructions
      ? this._sanitizer.bypassSecurityTrustHtml(singleLoginInstructions)
      : '';
  }

  hasAlternativeAuthMethods(config: GetConfigByCityIdOutput) {
    return (
      config.methods.filter(
        (method) =>
          method.strategy !== 'CREDENTIALS' && method.strategy !== 'INTEGRATION'
      ).length > 0
    );
  }

  showGovBrMethod(config: GetConfigByCityIdOutput) {
    return config.methods.some(
      (method) => method.strategy === 'GOV_BR' && method.config?.['url']
    );
  }

  showBbMethod(config: GetConfigByCityIdOutput) {
    return config.methods.some(
      (method) =>
        method.strategy === 'BANCO_DO_BRASIL' && method.config?.['url']
    );
  }

  showIntegrationLoginButton(config: GetConfigByCityIdOutput) {
    return config.methods.some(
      (method) => method.strategy === 'INTEGRATION' && method.config?.['url']
    );
  }

  integrationMethodUrl(config: GetConfigByCityIdOutput) {
    return config.methods.find((method) => method.strategy === 'INTEGRATION')
      ?.config?.['url'];
  }

  loginButtonText(config: GetConfigByCityIdOutput) {
    return config.methods.find((method) => method.strategy === 'INTEGRATION')
      ?.config?.['loginButtonText'];
  }

  redirectToIntegrationUrl(config: GetConfigByCityIdOutput) {
    const integrationMethodUrl = this.integrationMethodUrl(config);
    if (integrationMethodUrl) {
      window.location = integrationMethodUrl;
    }
  }

  copyIntegrationMethodUrl(config: GetConfigByCityIdOutput) {
    const integrationMethodUrl = this.integrationMethodUrl(config);
    if (integrationMethodUrl) {
      navigator.clipboard.writeText(integrationMethodUrl).then(() => {
        this.urlCopied.set(true);

        const resetCopyToClipboardTimeout = setTimeout(() => {
          this.urlCopied.set(false);
          clearTimeout(resetCopyToClipboardTimeout);
        }, 3000);
      });
    }
  }

  getEmailFromQueryParam(): string {
    const queryParams = this._route.snapshot.queryParams;
    return queryParams['email'];
  }

  socialLogin(user: SocialUser) {
    const code = user.authToken ?? user.idToken;
    const provider = user.provider;

    this._appHandler.setLoading(true, {
      title: 'Carregando...',
      description: 'Estamos verificando as informações do Google.',
    });

    void this._router.navigate([`/${this.cityIdentity}/callback`], {
      queryParams: {
        code,
        provider,
        state: buildEncodedState(this.customUrlRedirect, 'SOCIAL'),
      },
    });
  }
}
